import { createSlice, createAction } from '@reduxjs/toolkit';

export const createTemplateRequest = createAction('CREATE_WHATSAPP_TEMPLATE');
export const loadTemplatesRequest = createAction('LOAD_WHATSAPP_TEMPLATES');
export const generateAITemplateRequest = createAction('GENERATE_AI_TEMPLATE');

const initialState = {
    templates: [],
    selectedTemplate: null,
    whatsappData: {
        to: '',
        body: '',
        header: ''
    },
    validationErrors: {
        to: '',
        body: '',
        templateParams: {
            header: {},
            body: {}
        }
    },
    isConnected: false,
    isPaid: false,
    wabaAccounts: [],
    phones: [],
    selectedPhone: null,
    wabaLoading: false,
    wabaError: null,
    wabaLastFetchedAt: null,
    loading: false,
    error: null,
    success: false,
    mediaVerification: {
        headerHandle: null,
        headerHandleError: null,
        isVerifying: false
    },
    aiGeneration: {
        loading: false,
        error: null,
        data: null,
    }
};

const whatsappSlice = createSlice({
    name: 'whatsapp',
    initialState,
    reducers: {
        selectTemplate: (state, action) => {
            const template = state.templates.find(t => t.id === action.payload);
            state.selectedTemplate = template;
            if (template) {
                state.whatsappData = {
                    to: state.whatsappData.to,
                    body: template.body,
                    header: template.headerText || ''
                };
            }
        },
        setWhatsappData: (state, action) => {
            state.whatsappData = {
                ...state.whatsappData,
                ...action.payload
            };
        },
        setRecipient: (state, action) => {
            state.whatsappData.to = action.payload;
        },
        resetWhatsappState: (state) => {
            state.selectedTemplate = null;
            state.whatsappData = {
                to: '',
                body: '',
                header: ''
            };
            state.validationErrors = {
                to: '',
                body: ''
            };
            state.error = null;
            state.success = false;
        },
        clearTemplate: (state) => {
            state.selectedTemplate = null;
            state.whatsappData = {
                to: state.whatsappData.to,
                body: '',
                header: ''
            };
            state.validationErrors = {
                to: '',
                body: ''
            };
        },
        setValidationErrors: (state, action) => {
            state.validationErrors = {
                ...state.validationErrors,
                ...action.payload
            };
        },
        clearValidationErrors: (state) => {
            state.validationErrors = {
                to: '',
                body: '',
                templateParams: {
                    header: {},
                    body: {}
                }
            };
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        setIsConnected: (state, action) => {
            state.isConnected = action.payload;
        },
        setIsPaid: (state, action) => {
            state.isPaid = action.payload;
        },
        setWabaAccounts: (state, action) => {
            state.wabaAccounts = Array.isArray(action.payload) ? action.payload : [];
            state.wabaLastFetchedAt = Date.now();
        },
        setWabaLoading: (state, action) => {
            state.wabaLoading = action.payload;
        },
        setWabaError: (state, action) => {
            state.wabaError = action.payload;
        },
        setPhones: (state, action) => {
            state.phones = Array.isArray(action.payload) ? action.payload : [];
            // Auto-select first phone if none selected yet
            if (!state.selectedPhone && Array.isArray(action.payload) && action.payload.length > 0) {
                state.selectedPhone = action.payload[0];
            }
        },
        setSelectedPhone: (state, action) => {
            state.selectedPhone = action.payload;
        },
        addTemplate: (state, action) => {
            const newTemplate = {
                id: Math.max(...state.templates.map(t => t.id), 0) + 1,
                ...action.payload
            };
            state.templates.push(newTemplate);
        },
        setTemplates: (state, action) => {
            state.templates = Array.isArray(action.payload) ? action.payload : [];
        },
        setMediaVerifying: (state, action) => {
            state.mediaVerification.isVerifying = action.payload;
        },
        setHeaderHandle: (state, action) => {
            state.mediaVerification.headerHandle = action.payload;
        },
        setHeaderHandleError: (state, action) => {
            state.mediaVerification.headerHandleError = action.payload;
        },
        clearMediaVerification: (state) => {
            state.mediaVerification = {
                headerHandle: null,
                headerHandleError: null,
                isVerifying: false
            };
        },
        generateAIStart: (state) => {
            state.aiGeneration = { loading: true, error: null, data: null };
        },
        generateAISuccess: (state, action) => {
            state.aiGeneration = { loading: false, error: null, data: action.payload };
        },
        generateAIFailure: (state, action) => {
            state.aiGeneration = { loading: false, error: action.payload, data: null };
        },
        resetAIGeneration: (state) => {
            state.aiGeneration = { loading: false, error: null, data: null };
        }
    }
});

export const {
    selectTemplate,
    setWhatsappData,
    setRecipient,
    resetWhatsappState,
    clearTemplate,
    setValidationErrors,
    clearValidationErrors,
    setLoading,
    setError,
    setSuccess,
    setIsConnected,
    setIsPaid,
    setWabaAccounts,
    setWabaLoading,
    setWabaError,
    setPhones,
    setSelectedPhone,
    addTemplate,
    setTemplates,
    setMediaVerifying,
    setHeaderHandle,
    setHeaderHandleError,
    clearMediaVerification,
    generateAIStart,
    generateAISuccess,
    generateAIFailure,
    resetAIGeneration
} = whatsappSlice.actions;

export default whatsappSlice.reducer;